import React from 'react';
import {
  MapPin, Briefcase, Mic, Languages, ArrowRight, UserCircle,
  Mail, Phone, Linkedin, Globe, Send,
} from 'lucide-react';
import { absoluteUrl } from '../lib/api';
import { modalityChipMeta } from '../pages/DirectoryPage';
import BioContent from './BioContent';

// Directional combinations (active×active + passive→active), minus excluded.
function derivePairs(active, passive, excluded) {
  const A = (active || []).filter(Boolean);
  const P = (passive || []).filter(Boolean);
  const ex = new Set(excluded || []);
  const out = [];
  for (let i = 0; i < A.length; i += 1) for (let j = 0; j < A.length; j += 1) if (i !== j) out.push([A[i], A[j]]);
  for (const p of P) for (const a of A) out.push([p, a]);
  const seen = new Set();
  return out.filter(([f, t]) => {
    if (!f || !t || f === t) return false;
    if (ex.has(`${f}→${t}`)) return false;
    const k = `${f}|${t}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

const STATUS_META = {
  candidate: { label: 'Candidato a miembro', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  senior:    { label: 'Miembro senior',      tone: 'bg-cmic-50 text-cmic-700 border-cmic-200' },
  honorary:  { label: 'Miembro honorario',   tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  associate: { label: 'Miembro asociado',    tone: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const SectionLabel = ({ icon: Icon, children, color }) => (
  <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 ${color === 'violet' ? 'text-violet-700' : 'text-cmic-600'}`}>
    <Icon size={12} /> {children}
  </p>
);

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0">
        <Icon size={15} className="text-cmic-600" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-charcoal-400 leading-none mb-0.5">{label}</p>
        <p className="text-xs text-charcoal-700 truncate">{value}</p>
      </div>
    </div>
  );
}

/**
 * Live directory-profile preview — mirrors the public InterpreterProfilePage
 * look (gradient header, overlapping photo, status badge, directional
 * combination cards and the colored contact action buttons), stacked to fit a
 * narrow editor/registration sidebar so registrants see exactly how their
 * public profile will look.
 */
export default function InterpreterProfilePreview({ form, label = 'Vista previa de tu perfil' }) {
  const fullName = (form.full_name || '').trim()
    || [form.nombres, form.apellido_paterno, form.apellido_materno].filter(Boolean).join(' ').trim()
    || 'Tu nombre';
  const initials = fullName.split(' ').slice(0, 2).map((n) => n.charAt(0)).join('').toUpperCase() || '?';
  const photo = form.photo_url ? absoluteUrl(form.photo_url) : '';
  const status = form.membership_status && form.membership_status !== 'active' ? STATUS_META[form.membership_status] : null;
  const specialty = Array.isArray(form.specialty)
    ? form.specialty
    : (form.specialty ? String(form.specialty).split(',').map((s) => s.trim()).filter(Boolean) : []);
  const modalities = (form.modalities || []).filter(Boolean);
  const active = (form.active_languages || []).filter(Boolean);
  const pairs = derivePairs(form.active_languages, form.passive_languages, form.excluded_language_pairs);
  const addEmails = (form.additional_emails || []).filter(Boolean);
  const addPhones = (form.additional_phones || []).filter(Boolean);
  const hasContact = !!(form.email || form.phone || form.city || form.linkedin_url || form.website || addEmails.length || addPhones.length);

  return (
    <div data-testid="interpreter-full-preview">
      {label && <p className="text-[11px] font-semibold text-charcoal-400 uppercase tracking-wider mb-3">{label}</p>}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Gradient header banner */}
        <div className="h-20 bg-gradient-to-br from-cmic-800 via-cmic-700 to-cmic-600 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-8 left-6 w-32 h-32 bg-white rounded-full blur-2xl" />
            <div className="absolute -bottom-10 right-4 w-40 h-40 bg-cmic-300 rounded-full blur-2xl" />
          </div>
        </div>

        {/* Header content */}
        <div className="px-5 pb-5">
          <div className="-mt-12 flex items-end gap-3 relative z-10">
            {photo ? (
              <img src={photo} alt={fullName} className="w-24 h-24 rounded-2xl object-cover object-top bg-gray-100 ring-4 ring-white shadow-md flex-shrink-0" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cmic-500 to-cmic-700 flex items-center justify-center ring-4 ring-white shadow-md flex-shrink-0">
                <span className="text-white text-2xl font-bold">{initials}</span>
              </div>
            )}
          </div>

          <div className="mt-3 flex items-start gap-2 flex-wrap">
            <h3 className="text-xl font-bold text-charcoal-800 leading-tight">{fullName}</h3>
            {status && (
              <span className={`inline-flex items-center text-[10px] font-semibold tracking-wide uppercase border rounded-full px-2 py-0.5 ${status.tone}`}>{status.label}</span>
            )}
          </div>
          {form.city && (
            <div className="inline-flex items-center gap-1.5 text-xs text-charcoal-500 mt-2 bg-gray-50 px-2.5 py-1 rounded-lg">
              <MapPin size={12} className="text-cmic-500" />{form.city}
            </div>
          )}

          {specialty.length > 0 && (
            <div className="mt-4">
              <SectionLabel icon={Briefcase}>Especialidades</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {specialty.map((s) => <span key={s} className="text-xs font-medium bg-cmic-50 text-cmic-700 border border-cmic-100 px-2.5 py-1 rounded-full">{s}</span>)}
              </div>
            </div>
          )}

          {modalities.length > 0 && (
            <div className="mt-4">
              <SectionLabel icon={Mic} color="violet">Modalidades de interpretación</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {modalities.map((m) => {
                  const meta = modalityChipMeta(m);
                  const I = meta.icon;
                  return (
                    <span key={m} className="inline-flex items-center gap-1 text-xs font-medium bg-violet-50 text-violet-700 border border-violet-100 px-2.5 py-1 rounded-full">
                      <I size={11} className="text-violet-500" />{meta.full}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {active.length > 0 && (
            <div className="mt-4">
              <SectionLabel icon={Languages}>Idiomas activos</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {active.map((l) => <span key={l} className="text-sm font-medium bg-cmic-100 text-cmic-700 px-3 py-1 rounded-full">{l}</span>)}
              </div>
              <p className="text-[10px] text-charcoal-400 mt-1.5">Interpreta hacia estos idiomas</p>
            </div>
          )}
        </div>

        {/* Body */}
        {(pairs.length > 0 || form.bio || hasContact) && (
          <div className="border-t border-gray-100 p-5 space-y-5">
            {pairs.length > 0 && (
              <div>
                <p className="text-sm font-bold text-charcoal-800 mb-3 flex items-center gap-1.5">
                  <Languages size={16} className="text-cmic-500" /> Combinación Lingüística
                  <span className="text-[11px] font-normal text-charcoal-400">· {pairs.length} {pairs.length === 1 ? 'combinación' : 'combinaciones'}</span>
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {pairs.slice(0, 14).map(([f, t]) => (
                    <div key={`${f}-${t}`} className="group flex items-center gap-2.5 p-2.5 rounded-lg bg-gradient-to-br from-white via-cmic-50/40 to-white border border-cmic-100 text-xs">
                      <div className="flex-1 min-w-0 text-right">
                        <span className="block text-[9px] uppercase tracking-wider text-cmic-500/80 font-semibold mb-0.5">Desde</span>
                        <span className="block font-semibold text-charcoal-800 truncate" title={f}>{f}</span>
                      </div>
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cmic-500 text-white flex items-center justify-center shadow-sm">
                        <ArrowRight size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-[9px] uppercase tracking-wider text-cmic-500/80 font-semibold mb-0.5">Hacia</span>
                        <span className="block font-semibold text-charcoal-800 truncate" title={t}>{t}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.bio && (
              <div>
                <p className="text-sm font-bold text-charcoal-800 mb-1.5 flex items-center gap-1.5"><UserCircle size={16} className="text-cmic-500" /> Semblanza</p>
                <BioContent bio={form.bio} className="text-xs text-charcoal-600 leading-relaxed" />
              </div>
            )}

            {hasContact && (
              <div>
                <p className="text-sm font-bold text-charcoal-800 mb-3">Información de Contacto</p>
                <div className="space-y-2.5">
                  {form.email && <ContactRow icon={Mail} label="Email" value={form.email} />}
                  {addEmails.map((e) => <ContactRow key={e} icon={Mail} label="Email adicional" value={e} />)}
                  {form.phone && <ContactRow icon={Phone} label="Teléfono" value={form.phone} />}
                  {addPhones.map((p) => <ContactRow key={p} icon={Phone} label="Teléfono adicional" value={p} />)}
                  {form.city && <ContactRow icon={MapPin} label="Ubicación" value={form.city} />}
                </div>

                <div className="mt-4 space-y-2">
                  {form.linkedin_url && (
                    <div className="flex items-center gap-2.5 bg-[#0077B5] text-white rounded-xl px-4 py-2.5 text-sm font-medium shadow-sm">
                      <Linkedin size={18} /> Ver perfil en LinkedIn
                    </div>
                  )}
                  {form.website && (
                    <div className="flex items-center gap-2.5 bg-charcoal-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium shadow-sm">
                      <Globe size={18} /> Visitar página web
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-cmic-600 to-cmic-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium shadow-sm">
                    <Send size={16} /> Solicitar este intérprete
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
